import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import useAuth from "../../hooks/useAuth";
import {
  getNumericBalance,
  loadAutoWithdrawConfig,
  saveAutoWithdrawConfig,
} from "../../utils/autoWithdraw";
import apiClient from "../../store/services/apiClient";
import styles from "./AutoWithdrawTab.module.css";

export default function AutoWithdrawTab() {
  const DEFAULT_COOLDOWN_SEC = 240;
  const DEPOSIT_POLL_INTERVAL_MS = 15000;
  const { user } = useAuth();
  const playerBalance = useSelector((state) => state.user.playerBalance);

  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState(1000);
  const [savedConfig, setSavedConfig] = useState({
    enabled: false,
    threshold: 0,
  });
  const [latestDepositAmount, setLatestDepositAmount] = useState(0);
  const [depositLoading, setDepositLoading] = useState(false);
  const [status, setStatus] = useState({
    type: "muted",
    text: "",
  });

  const uid = user?.id;
  const balance = useMemo(
    () => getNumericBalance(playerBalance),
    [playerBalance],
  );
  const minThreshold = useMemo(
    () => Math.max(0, Number(latestDepositAmount || 0) * 2),
    [latestDepositAmount],
  );
  const savedThreshold = useMemo(
    () => Math.max(0, Number(savedConfig?.threshold || 0)),
    [savedConfig],
  );
  const hasUnsavedChanges = useMemo(
    () =>
      Boolean(enabled) !== Boolean(savedConfig?.enabled) ||
      Math.max(0, Number(threshold || 0)) !== savedThreshold,
    [enabled, savedConfig, savedThreshold, threshold],
  );

  const buildDepositSignature = (deposit) => {
    const amount = Math.max(0, Number(deposit?.amount || 0));
    const messageText = String(deposit?.messageText || "").trim();
    const createdAt = String(deposit?.created_at || "").trim();

    if (!amount && !messageText && !createdAt) {
      return "";
    }

    return [amount, messageText, createdAt].join("|");
  };

  useEffect(() => {
    if (!uid) return;

    let isMounted = true;
    const cfg = loadAutoWithdrawConfig(uid);
    setEnabled(Boolean(cfg.enabled));
    setThreshold(Number(cfg.threshold || 0));
    setSavedConfig({
      enabled: Boolean(cfg.enabled),
      threshold: Number(cfg.threshold || 0),
    });
    setLatestDepositAmount(Number(cfg.latestDepositAmount || 0));

    if (cfg.lastMessage) {
      setStatus({
        type: cfg.lastStatus === "success" ? "success" : "warning",
        text: cfg.lastMessage,
      });
    }

    const fetchLatestDeposit = async () => {
      setDepositLoading(true);
      try {
        const response = await apiClient.get(
          `/userbalance/getturnoverdata/${uid}`,
        );
        const depositData = response?.data || {};
        const latestAmount = Math.max(0, Number(depositData?.amount || 0));
        const latestSignature = buildDepositSignature(depositData);
        const latestMessageText = String(depositData?.messageText || "");
        const latestCreatedAt = String(depositData?.created_at || "");
        const nextMinThreshold = Math.max(0, latestAmount * 2);
        if (!isMounted) return;

        setLatestDepositAmount(latestAmount);

        const prevConfig = loadAutoWithdrawConfig(uid);
        const depositChanged =
          latestSignature &&
          latestSignature !== String(prevConfig?.latestDepositSignature || "");

        const baseNextConfig = {
          ...prevConfig,
          latestDepositAmount: latestAmount,
          latestDepositSignature: latestSignature,
          latestDepositMessageText: latestMessageText,
          latestDepositCreatedAt: latestCreatedAt,
          minThreshold: nextMinThreshold,
        };

        if (depositChanged && prevConfig?.enabled && nextMinThreshold > 0) {
          const syncedConfig = {
            ...baseNextConfig,
            threshold: nextMinThreshold,
            lastStatus: "success",
            lastMessage: `พบยอดฝากล่าสุด ${latestAmount.toLocaleString()} บาท ระบบปรับยอดตั้งถอนเป็น ${nextMinThreshold.toLocaleString()} บาทอัตโนมัติ`,
          };

          saveAutoWithdrawConfig(uid, syncedConfig);
          setSavedConfig({
            enabled: Boolean(syncedConfig.enabled),
            threshold: Number(syncedConfig.threshold || 0),
          });
          setThreshold(nextMinThreshold);
          setStatus({
            type: "success",
            text: syncedConfig.lastMessage,
          });
          publishConfigChanged();
        } else {
          const shouldPersistDepositMeta =
            latestAmount !== Number(prevConfig?.latestDepositAmount || 0) ||
            latestSignature !==
              String(prevConfig?.latestDepositSignature || "") ||
            latestMessageText !==
              String(prevConfig?.latestDepositMessageText || "") ||
            latestCreatedAt !==
              String(prevConfig?.latestDepositCreatedAt || "") ||
            nextMinThreshold !== Number(prevConfig?.minThreshold || 0);

          if (shouldPersistDepositMeta) {
            saveAutoWithdrawConfig(uid, baseNextConfig);
            publishConfigChanged();
          }
        }
      } catch (error) {
        if (!isMounted) return;
        setLatestDepositAmount(0);
      } finally {
        if (isMounted) {
          setDepositLoading(false);
        }
      }
    };

    fetchLatestDeposit();

    const intervalId = window.setInterval(
      fetchLatestDeposit,
      DEPOSIT_POLL_INTERVAL_MS,
    );

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [uid]);

  useEffect(() => {
    if (!uid || minThreshold <= 0) return;

    setThreshold((prev) => {
      const current = Number(prev) || 0;
      if (current <= 0 || current < minThreshold) {
        return minThreshold;
      }
      return current;
    });
  }, [uid, minThreshold]);

  const publishConfigChanged = () => {
    try {
      window.dispatchEvent(
        new CustomEvent("app:autoWithdrawConfigChanged", {
          detail: { userId: uid },
        }),
      );
    } catch {
      // ignore
    }
  };

  const handleSave = () => {
    if (!uid) {
      setStatus({ type: "warning", text: "กรุณาเข้าสู่ระบบก่อน" });
      return;
    }

    const normalizedThreshold = Math.max(0, Number(threshold) || 0);
    const normalizedLatestDeposit = Math.max(
      0,
      Number(latestDepositAmount || 0),
    );
    const requiredMin = Math.max(0, Number(minThreshold || 0));

    if (enabled && normalizedLatestDeposit <= 0) {
      setStatus({
        type: "warning",
        text: "ไม่พบยอดฝากล่าสุด กรุณาฝากเงินก่อนตั้งถอนล่วงหน้า",
      });
      return;
    }

    if (enabled && normalizedThreshold < requiredMin) {
      setStatus({
        type: "warning",
        text: `ยอดถอนล่วงหน้าขั้นต่ำ ${requiredMin.toLocaleString()} บาท (ฝากล่าสุด ${normalizedLatestDeposit.toLocaleString()} บาท)`,
      });
      return;
    }

    const normalizedCooldown = DEFAULT_COOLDOWN_SEC;

    const prev = loadAutoWithdrawConfig(uid);
    const next = {
      ...prev,
      enabled,
      threshold: normalizedThreshold,
      latestDepositAmount: normalizedLatestDeposit,
      minThreshold: requiredMin,
      cooldownSec: normalizedCooldown,
      lastStatus: "idle",
      lastMessage: enabled
        ? `เปิดถอนอัตโนมัติแล้ว (ยอดตั้งไว้ ${normalizedThreshold.toLocaleString()} บาท)`
        : "ปิดถอนอัตโนมัติแล้ว",
    };

    saveAutoWithdrawConfig(uid, next);
    setSavedConfig({
      enabled: Boolean(next.enabled),
      threshold: Number(next.threshold || 0),
    });
    publishConfigChanged();

    setStatus({
      type: enabled ? "success" : "muted",
      text: next.lastMessage,
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          <Icon icon="solar:alarm-bold-duotone" width={20} /> ตั้งถอนอัตโนมัติ
        </h3>
        <div className={styles.balanceChip}>
          ยอดปัจจุบัน {Number(balance || 0).toLocaleString()} บาท
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.row}>
          <div className={styles.statusIndicator}>
            <span
              className={`${styles.statusDot} ${enabled ? styles.statusDotOn : styles.statusDotOff}`}
              aria-hidden="true"
            />
            <div>
              <div className={styles.label}>
                เปิดใช้งานถอนอัตโนมัติ {enabled ? "(เปิด)" : "(ปิด)"}
              </div>
              <p className={styles.hint}>
                เมื่อยอดเงินถึงตามที่ตั้ง ระบบจะส่งคำขอถอนให้เหมือนเมนูถอนเงิน
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setEnabled((v) => !v)}
            className={`${styles.switch} ${enabled ? styles.switchOn : styles.switchOff}`}
            aria-label="toggle auto withdraw"
          >
            <span className={styles.switchDot} />
          </button>
        </div>

        <div className={styles.summaryBox}>
          <div className={styles.summaryLabel}>ยอดที่ตั้งไว้ตอนนี้</div>
          <div className={styles.summaryValue}>
            {savedThreshold > 0
              ? `${savedThreshold.toLocaleString()} บาท`
              : "ยังไม่มีการตั้งค่า"}
          </div>
          <div className={styles.hint}>
            สถานะปัจจุบัน:{" "}
            {savedConfig.enabled ? "เปิดใช้งานอยู่" : "ปิดใช้งานอยู่"}
          </div>
          {hasUnsavedChanges ? (
            <div className={styles.pendingHint}>
              ค่าที่กำลังแก้ไขยังไม่ได้บันทึก
            </div>
          ) : null}
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="aw-threshold">
            ยอดที่ต้องการให้ถอนอัตโนมัติ (บาท)
          </label>
          <input
            id="aw-threshold"
            type="number"
            min={minThreshold > 0 ? minThreshold : 0}
            className={styles.input}
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            placeholder={
              minThreshold > 0
                ? `ขั้นต่ำ ${minThreshold.toLocaleString()}`
                : "รอดึงยอดฝากล่าสุด"
            }
          />
        </div>

        <p className={styles.hint}>
          {depositLoading ? (
            "กำลังดึงยอดฝากล่าสุด..."
          ) : (
            <>
              ตั้งถอนล่วงหน้า 1 เท่าขึ้นไปของยอดฝาก <br /> ตัวอย่าง <br />( ฝาก
              300 ตั้งถอนล่วงหน้าได้ 600 บาทขึ้นไปค่ะ )
            </>
          )}
        </p>

        <div className={styles.actions}>
          <button type="button" className={styles.saveBtn} onClick={handleSave}>
            บันทึกการตั้งค่า
          </button>
        </div>

        {status.text ? (
          <div
            className={`${styles.status} ${
              status.type === "success"
                ? styles.success
                : status.type === "warning"
                  ? styles.warning
                  : styles.muted
            }`}
          >
            {status.text}
          </div>
        ) : null}
      </div>
    </div>
  );
}
